import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, BarChart3, CheckCircle2, DollarSign, Gauge, Globe, Search, ShieldCheck, ShoppingBag, Sparkles, XCircle } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { withCredentials: true };
const GOLD = "#D4AF37";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "acquisition", label: "Acquisition", icon: Globe },
  { id: "seo", label: "SEO", icon: Search },
  { id: "store", label: "Store", icon: ShoppingBag },
  { id: "margins", label: "Margins", icon: DollarSign },
  { id: "ambassadors", label: "Ambassadors", icon: Sparkles },
  { id: "compliance", label: "Compliance", icon: ShieldCheck },
  { id: "health", label: "Health", icon: BarChart3 },
];
const RANGES = [{ d: 7, label: "7D" }, { d: 30, label: "30D" }, { d: 90, label: "90D" }];

const Panel = ({ title, children, testid }) => (
  <div className="border border-zinc-800 bg-[#0C0C0C] p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-4">{title}</p>
    {children}
  </div>
);

const Metric = ({ label, value, sub, accent, testid }) => (
  <div className="border border-zinc-800 bg-[#0C0C0C] p-5" data-testid={testid}>
    <p className={`font-serif text-3xl ${accent ? "text-[#D4AF37]" : ""}`}>{value ?? "—"}</p>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">{label}</p>
    {sub && <p className="text-[10px] text-zinc-600 mt-1.5 leading-snug">{sub}</p>}
  </div>
);

const Table = ({ cols, rows, empty, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    {rows?.length ? (
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[#52525B] font-mono text-[9px] uppercase tracking-[0.15em]">
          {cols.map((c) => <th key={c.key} className={`py-2 pr-3 ${c.num ? "text-right" : ""}`}>{c.label}</th>)}
        </tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t border-zinc-900 text-[#A1A1AA]">
            {cols.map((c) => <td key={c.key} className={`py-2 pr-3 ${c.num ? "text-right font-mono" : ""} ${c.gold ? "text-[#D4AF37]" : ""}`}>{r[c.key] ?? "—"}</td>)}
          </tr>))}
        </tbody>
      </table>
    ) : <p className="text-xs text-zinc-600 py-3">{empty || "No data recorded in this range yet."}</p>}
  </div>
);

const chartStyle = { background: "#0C0C0C", border: "1px solid #27272A", fontFamily: "monospace", fontSize: 11 };
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "never");
const Bool = ({ ok, label }) => (
  <div className="flex items-center gap-2 text-xs text-[#A1A1AA]">
    {ok ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-400" />}
    {label} <span className={`font-mono text-[9px] uppercase ${ok ? "text-emerald-400" : "text-red-400"}`}>{ok ? "connected" : "not configured"}</span>
  </div>
);

export default function CommandTab() {
  const [section, setSection] = useState("overview");
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState({ on: false, start: "", end: "" });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (sec, d, c) => {
    setLoading(true); setError(null);
    try {
      const params = c.on && c.start && c.end ? { start: c.start, end: c.end } : { days: d };
      const { data: res } = await axios.get(`${API}/command/${sec}`, { ...AUTH, params });
      setData((prev) => ({ ...prev, [sec]: res }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this section — check your connection and try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(section, days, custom); }, [section, days, custom, load]);

  const d = data[section];

  return (
    <div data-testid="command-tab">
      {/* Section nav + range filter */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setSection(id)} data-testid={`command-section-${id}`}
            className={`flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] px-3.5 py-2 border transition-colors ${section === id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA] hover:border-[#D4AF37]/50"}`}>
            <Icon size={11} /> {label}
          </button>
        ))}
        {section !== "health" && section !== "margins" && (
          <div className="ml-auto flex items-center gap-1.5">
            {RANGES.map((r) => (
              <button key={r.d} onClick={() => { setCustom({ on: false, start: "", end: "" }); setDays(r.d); }}
                data-testid={`command-range-${r.d}`}
                className={`font-mono text-[9px] px-3 py-2 border ${!custom.on && days === r.d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA]"}`}>
                {r.label}
              </button>
            ))}
            <button onClick={() => setCustom((c) => ({ ...c, on: !c.on }))} data-testid="command-range-custom"
              className={`font-mono text-[9px] px-3 py-2 border ${custom.on ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA]"}`}>
              Custom
            </button>
            {custom.on && (
              <>
                <input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
                  data-testid="command-range-start" className="bg-black border border-zinc-800 text-[#A1A1AA] text-[10px] font-mono px-2 py-1.5" />
                <input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
                  data-testid="command-range-end" className="bg-black border border-zinc-800 text-[#A1A1AA] text-[10px] font-mono px-2 py-1.5" />
              </>
            )}
          </div>
        )}
      </div>

      {d?.range && <p className="font-mono text-[9px] text-zinc-600 mb-4" data-testid="command-range-label">Showing {d.range.start} → {d.range.end} (UTC)</p>}
      {loading && <p className="font-mono text-xs text-[#D4AF37] py-10 text-center animate-pulse" data-testid="command-loading">Loading {section}…</p>}
      {error && !loading && (
        <div className="border border-red-900/50 bg-red-950/20 p-4 flex items-center gap-3 mb-4" data-testid="command-error">
          <AlertTriangle size={15} className="text-red-400" /><p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {!loading && !error && section === "overview" && d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
            <Metric label="Page Views" value={d.page_views?.toLocaleString()} sub={`prev period: ${d.previous?.page_views?.toLocaleString()}`} testid="cmd-ov-views" />
            <Metric label="Sessions" value={d.sessions?.toLocaleString()} sub={d.definitions?.sessions} testid="cmd-ov-sessions" />
            <Metric label="Unique Visitors" value={d.unique_visitors?.toLocaleString()} sub={`${d.returning_visitors} returning`} testid="cmd-ov-visitors" />
            <Metric label="Conversion" value={`${d.conversion_rate}%`} accent sub={`${d.paid_orders} paid orders (prev: ${d.previous?.paid_orders}) ÷ sessions`} testid="cmd-ov-conversion" />
          </div>
          <div className="grid grid-cols-2 gap-px">
            <Metric label="Engaged Sessions" value={d.engaged_sessions?.toLocaleString()} sub={d.definitions?.engaged_sessions} testid="cmd-ov-engaged" />
            <Metric label="Paid Orders" value={d.paid_orders?.toLocaleString()} testid="cmd-ov-orders" />
          </div>
          <Panel title="Daily Traffic Trend" testid="cmd-ov-trend">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" vertical={false} />
                <XAxis dataKey="day" stroke="#52525B" fontSize={9} tickLine={false} />
                <YAxis stroke="#52525B" fontSize={9} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={chartStyle} cursor={{ fill: "rgba(212,175,55,0.06)" }} />
                <Bar dataKey="views" fill={GOLD} name="Views" radius={[2, 2, 0, 0]} />
                <Bar dataKey="sessions" fill="#3f6212" name="Sessions" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {!loading && !error && section === "acquisition" && d && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title="Traffic Channels (sessions by first touch)" testid="cmd-acq-channels">
            <Table cols={[{ key: "channel", label: "Channel", gold: true }, { key: "sessions", label: "Sessions", num: true }]} rows={d.channels} />
          </Panel>
          <Panel title="Referring Domains" testid="cmd-acq-referrers">
            <Table cols={[{ key: "domain", label: "Domain" }, { key: "sessions", label: "Sessions", num: true }]} rows={d.referring_domains} empty="No external referrers in this range." />
          </Panel>
          <Panel title="Landing Pages" testid="cmd-acq-landing">
            <Table cols={[{ key: "path", label: "Path" }, { key: "sessions", label: "Sessions", num: true }]} rows={d.landing_pages} />
          </Panel>
          <Panel title="Campaigns & Ambassador Links" testid="cmd-acq-campaigns">
            <Table cols={[{ key: "campaign", label: "UTM Campaign" }, { key: "sessions", label: "Sessions", num: true }]} rows={d.campaigns} empty="No UTM-tagged campaign traffic in this range." />
            <div className="mt-4">
              <Table cols={[{ key: "code", label: "Ambassador Code", gold: true }, { key: "tagged_visits", label: "Tagged Visits", num: true }]} rows={d.ambassador_links} empty="No ambassador-link visits in this range." />
            </div>
            <p className="text-[10px] text-zinc-600 mt-3">{d.attribution_quality?.note} · {d.attribution_quality?.sessions_with_session_id}/{d.attribution_quality?.sessions_total} sessions carry a session id.</p>
          </Panel>
        </div>
      )}

      {!loading && !error && section === "seo" && d && (
        d.connected === false ? (
          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-6 max-w-3xl" data-testid="cmd-seo-setup">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Google Search Console — not connected</p>
            <p className="text-xs text-[#A1A1AA] mb-4">{d.reason === "invalid_credentials" ? "The configured credentials could not be parsed — re-add the service-account JSON." : "The property is verified, but the backend needs read-only API credentials. One-time setup:"}</p>
            <ol className="space-y-2 text-xs text-[#A1A1AA]">{(d.setup || []).map((s) => <li key={s}>{s}</li>)}</ol>
            {d.property_hint && <p className="text-[10px] text-zinc-500 mt-4">Property identifier: {d.property_hint}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono text-zinc-500" data-testid="cmd-seo-status">
              <span className="text-emerald-400">● Connected</span>
              <span>{d.property}</span>
              <span>Last sync: {fmtTime(d.last_sync)}</span>
              {d.stale && <span className="text-amber-400">⚠ showing cached data ({d.error})</span>}
              {!d.stale && d.error && <span className="text-red-400">⚠ {d.error === "quota_or_temporary" ? "Google quota/temporary limit — retry later" : d.error === "authorization_failed" ? "Authorization failed — re-check service-account permission on the property" : "Google API error"}</span>}
            </div>
            {d.totals ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
                  <Metric label="Clicks" value={d.totals.clicks?.toLocaleString()} accent testid="cmd-seo-clicks" />
                  <Metric label="Impressions" value={d.totals.impressions?.toLocaleString()} testid="cmd-seo-impressions" />
                  <Metric label="CTR" value={`${d.totals.ctr}%`} testid="cmd-seo-ctr" />
                  <Metric label="Avg Position" value={d.totals.position} testid="cmd-seo-position" />
                </div>
                <Panel title="Search Performance by Date" testid="cmd-seo-trend">
                  {d.by_date?.length ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={d.by_date}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" vertical={false} />
                        <XAxis dataKey="date" stroke="#52525B" fontSize={9} tickLine={false} />
                        <YAxis stroke="#52525B" fontSize={9} tickLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={chartStyle} />
                        <Line type="monotone" dataKey="clicks" stroke={GOLD} dot={false} strokeWidth={2} />
                        <Line type="monotone" dataKey="impressions" stroke="#3f6212" dot={false} strokeWidth={1.5} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-zinc-600">No search data for this range yet — new properties can take days to accumulate data, and Google reporting lags ~2 days.</p>}
                </Panel>
                <div className="grid lg:grid-cols-2 gap-4">
                  <Panel title="Top Queries" testid="cmd-seo-queries">
                    <Table cols={[{ key: "query", label: "Query", gold: true }, { key: "clicks", label: "Clicks", num: true }, { key: "impressions", label: "Impr.", num: true }, { key: "ctr", label: "CTR %", num: true }, { key: "position", label: "Pos.", num: true }]} rows={d.top_queries} empty="No query data yet (low volume or reporting delay)." />
                  </Panel>
                  <Panel title="Top Landing Pages (search)" testid="cmd-seo-pages">
                    <Table cols={[{ key: "page", label: "Page" }, { key: "clicks", label: "Clicks", num: true }, { key: "impressions", label: "Impr.", num: true }, { key: "ctr", label: "CTR %", num: true }]} rows={d.top_pages} empty="No page data yet." />
                  </Panel>
                </div>
              </>
            ) : <p className="text-xs text-zinc-600" data-testid="cmd-seo-nodata">Connected, but no data could be retrieved{d.error ? ` (${d.error})` : ""}.</p>}
            <Panel title="Sitemap & Indexing" testid="cmd-seo-sitemaps">
              {d.sitemaps === null || d.sitemap_error ? (
                <p className="text-xs text-amber-400">Sitemap status unavailable ({d.sitemap_error || "API error"}).</p>
              ) : d.sitemaps?.length ? (
                <Table cols={[{ key: "path", label: "Sitemap" }, { key: "last_submitted", label: "Submitted" }, { key: "last_downloaded", label: "Last Crawled" }, { key: "errors", label: "Errors", num: true }, { key: "warnings", label: "Warnings", num: true }]} rows={d.sitemaps} />
              ) : (
                <p className="text-xs text-amber-400">No sitemap submitted to Search Console. Action: submit https://molecularixbiolabs.com/sitemap.xml in Search Console → Sitemaps. Verification alone does not guarantee indexing.</p>
              )}
              <p className="text-[10px] text-zinc-600 mt-2">{d.reporting_delay_note}</p>
            </Panel>
          </div>
        )
      )}

      {!loading && !error && section === "store" && d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
            <Metric label="Product Views" value={d.product_views?.toLocaleString()} testid="cmd-store-views" />
            <Metric label="Revenue" value={d.revenue != null ? `$${d.revenue.toLocaleString()}` : "—"} accent sub={d.definitions?.revenue} testid="cmd-store-revenue" />
            <Metric label="Avg Order Value" value={d.aov != null ? `$${d.aov}` : "—"} sub={d.aov == null ? "No paid orders in range" : null} testid="cmd-store-aov" />
            <Metric label="Paid / Cancelled" value={`${d.orders?.paid} / ${d.orders?.cancelled}`} sub={`${d.orders?.pending} pending · ${d.orders?.total} total`} testid="cmd-store-orders" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Conversion Funnel (distinct sessions per stage)" testid="cmd-store-funnel">
              {(d.funnel || []).map((f, i) => {
                const max = d.funnel[0]?.count || 1;
                return (
                  <div key={f.stage} className="mb-2.5">
                    <div className="flex justify-between text-xs text-[#A1A1AA] mb-1"><span>{f.stage}</span><span className="font-mono">{f.count}</span></div>
                    <div className="h-2 bg-zinc-900"><div className="h-2" style={{ width: `${Math.max(2, (f.count / max) * 100)}%`, background: i === d.funnel.length - 1 ? "#10b981" : GOLD, opacity: 1 - i * 0.12 }} /></div>
                  </div>
                );
              })}
              <p className="text-[10px] text-zinc-600 mt-2">{d.definitions?.funnel}</p>
            </Panel>
            <Panel title="Most-Viewed Compounds" testid="cmd-store-top">
              <Table cols={[{ key: "name", label: "Compound", gold: true }, { key: "views", label: "Views", num: true }]} rows={d.top_products} empty="No product-view events in this range yet (event tracking began with the Command Center)." />
            </Panel>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
            <Metric label="In Stock" value={d.stock?.in_stock} testid="cmd-store-instock" />
            <Metric label="Sold Out" value={d.stock?.sold_out} testid="cmd-store-soldout" />
            <Metric label="Low Stock" value={d.stock?.low_stock} testid="cmd-store-lowstock" />
            <Metric label="Add-to-Cart Events" value={d.add_to_cart_events} testid="cmd-store-atc" />
          </div>
        </div>
      )}

      {!loading && !error && section === "ambassadors" && d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px">
            <Metric label="Active Ambassadors" value={d.ambassadors?.length} testid="cmd-amb-count" />
            <Metric label="Pending Applications" value={d.pending_applications} accent testid="cmd-amb-pending" />
            <Metric label="Payouts in Range" value={d.payouts_in_range ? `$${d.payouts_in_range.total} (${d.payouts_in_range.count})` : "—"} testid="cmd-amb-payouts" />
          </div>
          <Panel title="Ambassador Performance" testid="cmd-amb-table">
            <Table cols={[
              { key: "code", label: "Code", gold: true }, { key: "name", label: "Name" },
              { key: "lifetime_clicks", label: "Clicks (lifetime)", num: true },
              { key: "tagged_visits_in_range", label: "Tagged Visits", num: true },
              { key: "qualified_orders", label: "Qualified Orders", num: true },
              { key: "attributed_revenue", label: "Revenue $", num: true },
              { key: "commission_earned", label: "Commission $", num: true },
              { key: "commission_unpaid", label: "Unpaid $", num: true },
            ]} rows={d.ambassadors} empty="No approved ambassadors yet." />
            <p className="text-[10px] text-zinc-600 mt-3">{d.definitions?.qualified_orders} · {d.definitions?.attribution_confidence}</p>
          </Panel>
        </div>
      )}

      {!loading && !error && section === "compliance" && d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
            <Metric label="Attestations (all time)" value={d.age_gate?.total_attestations?.toLocaleString()} testid="cmd-comp-att" />
            <Metric label="Attestations (range)" value={d.age_gate?.in_range?.toLocaleString()} testid="cmd-comp-att-range" />
            <Metric label="Checkout Consent" value={`${d.consent?.orders_with_checkout_consent}/${d.consent?.orders_total}`} sub="Orders with recorded Terms & RUO consent" testid="cmd-comp-consent" />
            <Metric label="Marketing Opt-outs" value={d.consent?.unsubscribed} sub={`${d.consent?.with_terms_consent}/${d.consent?.subscribers} subscribers consented`} testid="cmd-comp-unsub" />
          </div>
          <Panel title="Age-Gate Audit Log Integrity" testid="cmd-comp-audit">
            <p className="text-xs text-[#A1A1AA]">First record: <span className="font-mono">{fmtTime(d.age_gate?.first_record)}</span> · Latest: <span className="font-mono">{fmtTime(d.age_gate?.latest_record)}</span></p>
            <p className="text-[10px] text-zinc-600 mt-1.5">{d.age_gate?.note}</p>
          </Panel>
          <Panel title="Suspicious Event Patterns" testid="cmd-comp-suspicious">
            <Table cols={[{ key: "visitor", label: "Visitor" }, { key: "type", label: "Event" }, { key: "day", label: "Day" }, { key: "events", label: "Count", num: true }]}
              rows={d.suspicious_patterns} empty="None detected in this range." />
            <p className="text-[10px] text-zinc-600 mt-2">{d.suspicious_note}</p>
          </Panel>
        </div>
      )}

      {!loading && !error && section === "margins" && d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px">
            <Metric label="Verified Margins" value={`${d.with_verified_margin}/${d.products}`} accent testid="cmd-mar-verified" />
            <Metric label="Avg Gross Margin" value={(() => { const v = (d.rows || []).filter((r) => r.gross_margin_pct != null); return v.length ? `${(v.reduce((s, r) => s + r.gross_margin_pct, 0) / v.length).toFixed(1)}%` : "—"; })()} testid="cmd-mar-avg" />
            <Metric label="Cost Discrepancies" value={d.cost_discrepancies} accent={d.cost_discrepancies > 0} sub="Owner-entered cost differs from supplier catalog — owner value is used" testid="cmd-mar-disc" />
          </div>
          <Panel title="Profitability by Compound (supplier kit = 10 vials)" testid="cmd-mar-table">
            <Table cols={[
              { key: "name", label: "Compound", gold: true }, { key: "strength", label: "" },
              { key: "retail_price", label: "Retail $", num: true }, { key: "unit_cost", label: "Cost/Vial $", num: true },
              { key: "profit_per_vial", label: "Profit/Vial $", num: true }, { key: "profit_per_kit", label: "Profit/Kit $", num: true },
              { key: "gross_margin_pct", label: "Margin %", num: true }, { key: "markup_pct", label: "Markup %", num: true },
              { key: "source_ref", label: "Source" },
            ]} rows={(d.rows || []).map((r) => ({ ...r, name: r.cost_discrepancy ? `⚠ ${r.name}` : r.name, gross_margin_pct: r.gross_margin_pct ?? "n/a", profit_per_vial: r.profit_per_vial ?? "—", profit_per_kit: r.profit_per_kit ?? "—", markup_pct: r.markup_pct ?? "—", unit_cost: r.unit_cost ?? "no cost" }))} />
            <p className="text-[10px] text-zinc-600 mt-3">{d.definitions?.gross_margin} · {d.definitions?.markup} · {d.definitions?.profit_per_kit}. Margins marked n/a have no matching supplier strength (owner review).</p>
          </Panel>
        </div>
      )}

      {!loading && !error && section === "health" && d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px">
            <Metric label="API Errors (24h)" value={d.api_errors?.last_24h} accent={d.api_errors?.last_24h > 0} testid="cmd-health-errors" />
            <Metric label="Checkouts Started (7d)" value={d.checkout_health?.checkout_sessions_7d} testid="cmd-health-checkouts" />
            <Metric label="Paid Orders (7d)" value={d.checkout_health?.paid_orders_7d} testid="cmd-health-paid" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Integration Status" testid="cmd-health-integrations">
              <div className="space-y-2.5">
                <Bool ok={d.integrations?.stripe} label="Stripe payments" />
                <Bool ok={d.integrations?.email_resend} label="Transactional email (Resend)" />
                <Bool ok={d.integrations?.paypal_payouts} label={`PayPal payouts (${d.integrations?.paypal_env || "no env"})`} />
                <Bool ok={d.integrations?.web_push} label="Web push notifications" />
                <Bool ok={d.integrations?.google_search_console} label="Google Search Console" />
              </div>
            </Panel>
            <Panel title="Data Freshness" testid="cmd-health-freshness">
              <div className="space-y-1.5 text-xs text-[#A1A1AA] font-mono">
                <p>Last pageview: {fmtTime(d.freshness?.last_pageview_at)}</p>
                <p>Last event: {fmtTime(d.freshness?.last_event_at)}</p>
                <p>Last daily digest: {fmtTime(d.freshness?.last_digest_at)}</p>
                <p>Last DB backup: {fmtTime(d.freshness?.last_backup_at)}</p>
                <p>Last GSC sync: {fmtTime(d.freshness?.gsc_last_sync)}</p>
              </div>
            </Panel>
          </div>
          <Panel title="Recent Server Errors" testid="cmd-health-recent-errors">
            <Table cols={[{ key: "at", label: "At" }, { key: "method", label: "Method" }, { key: "path", label: "Path" }, { key: "status", label: "Status", num: true }, { key: "error", label: "Detail" }]}
              rows={d.api_errors?.recent} empty="No server errors recorded (tracking began with the Command Center)." />
            <p className="text-[10px] text-zinc-600 mt-2">{d.checkout_health?.note}</p>
          </Panel>
        </div>
      )}
    </div>
  );
}
